import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KanbanSquare, Euro, Trophy, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ETAPES = [
  { id: "prospect_qualifie", label: "Prospect qualifié", color: "hsl(220 48% 17%)" },
  { id: "appel_a_effectuer", label: "Appel à effectuer", color: "hsl(210 80% 45%)" },
  { id: "devis_a_preparer", label: "Devis à préparer", color: "hsl(33 76% 45%)" },
  { id: "devis_envoye", label: "Devis envoyé", color: "hsl(39 63% 46%)" },
  { id: "negociation", label: "Négociation", color: "hsl(280 40% 50%)" },
  { id: "gagne", label: "Gagné", color: "hsl(140 60% 35%)" },
  { id: "perdu", label: "Perdu", color: "hsl(0 60% 45%)" },
];

const eur = (v) => (v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " €";

export default function Pipeline() {
  const { user } = useAuth();
  const canAssign = ["admin", "responsable_commercial"].includes(user?.role);
  const [opps, setOpps] = useState(null);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [pending, setPending] = useState(null);

  const load = () => api.get("/opportunities").then(({ data }) => setOpps(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data)).catch(() => {}); }, []);

  const move = async (opp, etape, extra = {}) => {
    try {
      await api.put(`/opportunities/${opp.id}`, { etape, ...extra });
      toast.success(`« ${opp.prospect_nom} » → ${ETAPES.find((e) => e.id === etape).label}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDrop = (e, etape) => {
    e.preventDefault();
    const opp = opps.find((o) => o.id === e.dataTransfer.getData("id"));
    if (!opp || opp.etape === etape) return;
    if (etape === "gagne" || etape === "perdu") setPending({ opp, etape, ca_gagne: opp.valeur_potentielle || 0, motif_perte: "" });
    else move(opp, etape);
  };

  const openDetail = (o) => {
    setForm({
      valeur_potentielle: o.valeur_potentielle ?? "", prochaine_action: o.prochaine_action || "",
      echeance: o.echeance ? o.echeance.slice(0, 10) : "", commentaire: "",
    });
    setDetail(o);
  };

  const saveDetail = async () => {
    try {
      await api.put(`/opportunities/${detail.id}`, {
        valeur_potentielle: parseFloat(form.valeur_potentielle) || 0,
        prochaine_action: form.prochaine_action,
        echeance: form.echeance || null,
        commentaire: form.commentaire || null,
      });
      toast.success("Opportunité mise à jour");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalOuvert = (opps || []).filter((o) => !["gagne", "perdu"].includes(o.etape)).reduce((s, o) => s + (o.valeur_potentielle || 0), 0);
  const totalGagne = (opps || []).filter((o) => o.etape === "gagne").reduce((s, o) => s + (o.ca_gagne ?? o.valeur_potentielle ?? 0), 0);

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display flex items-center gap-2 text-3xl font-semibold sm:text-4xl"><KanbanSquare className="text-accent" /> Pipeline commercial</h1>
          <p className="mt-1 text-sm text-muted-foreground">Glissez-déposez les opportunités d'une étape à l'autre, du lead qualifié jusqu'au contrat gagné.</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="secondary" className="text-sm" data-testid="pipeline-open-value"><Euro size={13} className="mr-1" /> En cours : {eur(totalOuvert)}</Badge>
          <Badge className="bg-green-700 text-sm text-white" data-testid="pipeline-won-value"><Trophy size={13} className="mr-1" /> Gagné : {eur(totalGagne)}</Badge>
        </div>
      </div>

      {opps === null ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {ETAPES.map((col) => {
            const items = opps.filter((o) => o.etape === col.id);
            const colTotal = items.reduce((s, o) => s + (col.id === "gagne" ? (o.ca_gagne ?? o.valeur_potentielle ?? 0) : (o.valeur_potentielle || 0)), 0);
            return (
              <div
                key={col.id}
                className="min-w-[240px] flex-1 rounded-lg bg-secondary/50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col.id)}
                data-testid={`kanban-col-${col.id}`}
              >
                <div className="rounded-t-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: col.color }}>
                  {col.label} <span className="float-right font-normal opacity-80">{items.length} · {eur(colTotal)}</span>
                </div>
                <div className="min-h-[120px] space-y-2 p-2">
                  {items.map((o) => (
                    <div
                      key={o.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("id", o.id)}
                      onClick={() => openDetail(o)}
                      className="cursor-grab rounded-md border bg-card p-2.5 text-sm shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
                      data-testid={`kanban-card-${o.id}`}
                    >
                      <p className="font-medium">{o.prospect_nom}</p>
                      <p className="text-xs text-muted-foreground">{o.secteur || ""} {o.ville && `· ${o.ville}`}</p>
                      <div className="mt-1.5 flex items-center justify-between text-xs">
                        <span className="font-semibold text-accent">{eur(o.etape === "gagne" ? (o.ca_gagne ?? o.valeur_potentielle) : o.valeur_potentielle)}</span>
                        <span className="text-muted-foreground">{o.commercial_nom || ""}</span>
                      </div>
                      {o.prochaine_action && <p className="mt-1 truncate text-xs text-muted-foreground">→ {o.prochaine_action}{o.echeance && ` (${new Date(o.echeance).toLocaleDateString("fr-FR")})`}</p>}
                      {o.motif_perte && <p className="mt-1 flex items-center gap-1 text-xs text-red-600"><XCircle size={11} /> {o.motif_perte}</p>}
                    </div>
                  ))}
                  {items.length === 0 && <p className="p-2 text-center text-xs text-muted-foreground">Déposer ici</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent data-testid="opp-detail-dialog">
            <DialogHeader><DialogTitle className="font-display">{detail.prospect_nom}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Valeur potentielle (€)</Label><Input type="number" className="mt-1" value={form.valeur_potentielle} onChange={(e) => setForm({ ...form, valeur_potentielle: e.target.value })} data-testid="opp-edit-value" /></div>
                <div><Label>Échéance</Label><Input type="date" className="mt-1" value={form.echeance} onChange={(e) => setForm({ ...form, echeance: e.target.value })} data-testid="opp-edit-due" /></div>
              </div>
              <div><Label>Prochaine action</Label><Input className="mt-1" value={form.prochaine_action} onChange={(e) => setForm({ ...form, prochaine_action: e.target.value })} data-testid="opp-edit-action" /></div>
              {canAssign && (
              <div>
                <Label>Commercial responsable</Label>
                <Select value={detail.commercial_id || ""} onValueChange={async (v) => { try { await api.put(`/opportunities/${detail.id}`, { commercial_id: v }); toast.success("Opportunité réattribuée"); load(); } catch (e) { toast.error(formatApiError(e)); } }}>
                  <SelectTrigger className="mt-1" data-testid="opp-assign-select"><SelectValue placeholder={detail.commercial_nom || "Choisir"} /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div><Label>Ajouter un commentaire</Label><Textarea rows={2} className="mt-1" value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} data-testid="opp-edit-comment" /></div>
              {(detail.commentaires || []).length > 0 && (
                <div className="max-h-32 space-y-1 overflow-y-auto rounded-md bg-secondary/50 p-2 text-xs">
                  {detail.commentaires.slice().reverse().map((c, i) => (
                    <p key={i}><span className="font-medium">{c.auteur}</span> ({new Date(c.date).toLocaleDateString("fr-FR")}) : {c.texte}</p>
                  ))}
                </div>
              )}
              <Button onClick={saveDetail} className="w-full" data-testid="opp-edit-save">Enregistrer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {pending && (
        <Dialog open onOpenChange={(o) => !o && setPending(null)}>
          <DialogContent data-testid="opp-close-dialog">
            <DialogHeader><DialogTitle className="font-display">{pending.etape === "gagne" ? "🏆 Contrat gagné" : "Opportunité perdue"} — {pending.opp.prospect_nom}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {pending.etape === "gagne" ? (
                <div><Label>Chiffre d'affaires gagné (€)</Label><Input type="number" className="mt-1" value={pending.ca_gagne} onChange={(e) => setPending({ ...pending, ca_gagne: e.target.value })} data-testid="opp-won-value-input" /></div>
              ) : (
                <div>
                  <Label>Motif de la perte</Label>
                  <Select value={pending.motif_perte} onValueChange={(v) => setPending({ ...pending, motif_perte: v })}>
                    <SelectTrigger className="mt-1" data-testid="opp-lost-reason-select"><SelectValue placeholder="Choisir un motif" /></SelectTrigger>
                    <SelectContent>
                      {["Prix trop élevé", "Concurrent retenu", "Projet abandonné", "Sans réponse", "Timing inadapté", "Autre"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                className="w-full"
                onClick={() => { move(pending.opp, pending.etape, pending.etape === "gagne" ? { ca_gagne: parseFloat(pending.ca_gagne) || 0 } : { motif_perte: pending.motif_perte || "Non précisé" }); setPending(null); }}
                data-testid="opp-close-confirm"
              >
                Confirmer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
